// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * palette.ts — every colour that carries meaning, in ONE place.
 *
 * This file exists because of principle #22: a shape can be drawn in the right
 * cell at the right size and still be invisible, and "screenshot it and look"
 * cannot see 1.14:1. Colour literals live here, not inline in the renderer, so
 * `tests/contrast.test.ts` can hold every one of them to >= 3:1 against every
 * surface it is actually painted on (WCAG 2.1 §1.4.11, the floor for a
 * non-text graphic).
 *
 * The piece hues are also spread across a wide LUMINANCE range, not only hue,
 * so they stay distinguishable in greyscale / for a colour-blind player.
 */

import { T_I, T_O, T_T, T_S, T_Z, T_J, T_L, T_INGOT, T_CARGO, T_BALLAST } from './pieces';

/** Surfaces a meaningful mark can be drawn on. */
export const SURFACE = {
  /** The page behind everything. */
  page: '#0b1622',
  /** Inside the well — where pieces live. */
  well: '#131f2d',
  /** HUD panels, next/hold slots. */
  panel: '#0f1a26',
} as const;

export type SurfaceName = keyof typeof SURFACE;

/** Marks drawn INSIDE the well (checked against `well`, and `panel` for previews). */
export const PIECE_COLORS: Record<number, string> = {
  [T_I]: '#f5e7c8', // sand      — lightest
  [T_O]: '#f2a93b', // amber
  [T_T]: '#ef6f6a', // coral
  [T_S]: '#3fbf94', // sea
  [T_Z]: '#5bb8f2', // sky
  [T_J]: '#a985e8', // violet
  [T_L]: '#7f96ad', // slate     — darkest
  [T_INGOT]: '#e2c96b', // heavy brass, unmistakable
  [T_CARGO]: '#c9962e', // banded cargo
  [T_BALLAST]: '#6e7f73', // iron ballast — light enough to SEE, which is the point
};

/**
 * Everything else on the play surface that means something.
 *
 * The three below that report STATE — trim, list, danger — are also separated
 * in LUMINANCE, not only hue. The first pass had the trim green and the listing
 * amber at 1.14:1: the single most important readout in the game, telling a
 * colour-blind player nothing and vanishing entirely in greyscale. Pairs that
 * can appear on the same element are pinned by contrast.test.ts.
 */
export const MARKS = {
  /** The ghost showing where the piece will really land, drift included. */
  ghost: '#8ea4b8',
  /** The well's rim when the stack is near the ceiling. Rim state, with trim. */
  danger: '#ff6b57',
  /** The "IN TRIM" light, the trimmed band, and the rim in trim. Brightest. */
  trim: '#5fe0a8',
  /** The list meter's needle when out of trim. Deliberately darker than trim. */
  list: '#e08b1f',
  /** Text on the play surface (scores popping off a clear). */
  pop: '#f5e7c8',
} as const;

/** UI colours off the play surface — not held to the 3:1 graphic bar here. */
export const UI = {
  text: '#dce7f0',
  dim: '#8ea4b8',
  accent: '#f2a93b',
  line: '#25374a',
} as const;

/** sRGB relative luminance (WCAG 2.1). Exported so the test uses one implementation. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const int = parseInt(m[1], 16);
  const ch = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** WCAG contrast ratio between two hex colours. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
